import { map } from "lodash";
import create from "zustand";
import { persist } from "zustand/middleware";

export enum CurrencyKey {
  hnt = "hnt",
  usd = "usd",
  eur = "eur",
  gbp = "gbp",
  cad = "cad",
  aud = "aud",
  brl = "brl",
  jpy = "jpy",
  dkk = "dkk",
  sek = "sek",
  chf = "chf",
  inr = "inr",
  cny = "cny",
  myr = "myr",
  sgd = "sgd",
  nzd = "nzd",
  btc = "btc",
  eth = "eth"
}

interface CurrencyOption {
  title: string;
  wiseCurrency?: string;
  paypalCurrency?: string;
}

// Paypal: https://developer.paypal.com/reference/currency-codes/
const currencies: { [key in keyof typeof CurrencyKey]: CurrencyOption } = {
  [CurrencyKey.hnt]: { title: "HNT" },
  [CurrencyKey.usd]: {
    title: "USD",
    wiseCurrency: "USD",
    paypalCurrency: "USD"
  },
  [CurrencyKey.eur]: {
    title: "EURO",
    wiseCurrency: "EUR",
    paypalCurrency: "EUR"
  },
  [CurrencyKey.gbp]: {
    title: "GBP",
    wiseCurrency: "GBP",
    paypalCurrency: "GBP"
  },
  [CurrencyKey.cad]: {
    title: "CAD",
    wiseCurrency: "CAD",
    paypalCurrency: "CAD"
  },
  [CurrencyKey.aud]: {
    title: "AUD",
    wiseCurrency: "AUD",
    paypalCurrency: "AUD"
  },
  [CurrencyKey.brl]: {
    title: "BRL",
    wiseCurrency: "BRL",
    paypalCurrency: "BRL"
  },
  [CurrencyKey.jpy]: {
    title: "JPY",
    wiseCurrency: "JPY"
  },
  [CurrencyKey.dkk]: {
    title: "DKK",
    wiseCurrency: "DKK",
    paypalCurrency: "DKK"
  },
  [CurrencyKey.sek]: {
    title: "SEK",
    wiseCurrency: "SEK",
    paypalCurrency: "SEK"
  },
  [CurrencyKey.chf]: {
    title: "CHF",
    wiseCurrency: "CHF",
    paypalCurrency: "CHF"
  },
  [CurrencyKey.inr]: { title: "INR", wiseCurrency: "INR" },
  [CurrencyKey.cny]: { title: "CNY", paypalCurrency: "CNY" },
  [CurrencyKey.myr]: {
    title: "MYR",
    wiseCurrency: "MYR",
    paypalCurrency: "MYR"
  },
  [CurrencyKey.sgd]: {
    title: "SGD",
    wiseCurrency: "SGD",
    paypalCurrency: "SGD"
  },
  [CurrencyKey.nzd]: {
    title: "NZD",
    wiseCurrency: "NZD",
    paypalCurrency: "NZD"
  },
  [CurrencyKey.btc]: { title: "BTC" },
  [CurrencyKey.eth]: { title: "ETH" }
};

type CurrencyState = {
  currency: CurrencyKey;
  currencyOptions: Array<{ key: string; title: string }>;
  setCurrency: (newCurrency: CurrencyKey) => void;
  getCurrencyTitle: (currencyKey: CurrencyKey) => string;
  getCurrencyOption: (currencyKey: CurrencyKey) => CurrencyOption;
  currencyDisplay: (
    currency: CurrencyKey,
    amount: number | undefined,
    config?:
      | {
          minimumFractionDigits?: number;
          maximumFractionDigits?: number;
        }
      | undefined
  ) => string | null;
};

const useCurrency = create<CurrencyState>(
  persist(
    (set, get) => ({
      currency: CurrencyKey.hnt,
      currencyOptions: map(currencies, ({ title }, key) => ({ key, title })),
      setCurrency: (newCurrency) => set({ currency: newCurrency }),
      getCurrencyTitle: (currencyKey) => currencies[currencyKey].title,
      getCurrencyOption: (currencyKey) => currencies[currencyKey],
      currencyDisplay: (
        currency: CurrencyKey,
        amount: number | undefined,
        { minimumFractionDigits, maximumFractionDigits } = {
          minimumFractionDigits: 2,
          maximumFractionDigits: 2
        }
      ) => {
        if (amount == null) return null;

        if ([CurrencyKey.btc, CurrencyKey.eth].includes(currency)) {
          return `${(amount * 1000).toLocaleString(undefined, {
            minimumFractionDigits,
            maximumFractionDigits
          })}m${get().getCurrencyTitle(currency)}`;
        }

        return amount.toLocaleString("en-US", {
          style: "currency",
          currency,
          minimumFractionDigits,
          maximumFractionDigits
        });
      }
    }),
    {
      name: "hotspotty_currency",
      version: 9
    }
  )
);

export default useCurrency;
